import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  Box,
  IconButton,
  useToast,
  Card,
  CardHeader,
  CardBody,
  Heading,
  HStack,
  Grid,
  GridItem,
  useBreakpointValue,
  Text,
  Textarea,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Switch,
  FormLabel,
  Flex,
} from '@chakra-ui/react';
import {
  PencilSimpleLine,
  CheckCircle,
  XCircle,
  Star,
} from '@phosphor-icons/react';
import { useSelector } from 'react-redux';
import { updateSite } from '../firebase/siteProfile';
import { useAuth } from '../hooks/useAuth';

// Import components
import SiteBasicInfo from './site/SiteBasicInfo';
import SiteImageManager from './site/SiteImageManager';
import SiteLocationSection from './site/SiteLocationSection';
import SiteTimeline from './site/SiteTimeline';
import SourceLinkedText from './site/SourceLinkedText';
import SiteGroundingInfo from './site/SiteGroundingInfo';
import { renumberInTextCitations } from '../utils/citationUtils';

// Helper function to extract first N sentences
const getFirstNSentences = (text, n = 2) => {
  if (!text || typeof text !== 'string') return '';
  // Regex to split by sentences, trying to be robust.
  const sentences = text.match(/[^.!?\s][^.!?]*(?:[.!?](?!['"]*\s|$)[^.!?]*)*[.!?]?['"]*(?=\s|$)/g) || [];
  return sentences.slice(0, n).join(' ').trim();
};

const sectionsConfig = [
    { field: 'overviewIntroduction', title: 'Overview / Introduction' },
    { field: 'discoveryAndExcavation', title: 'Discovery and Excavation' },
    { field: 'historicalContextAndChronology_narrative', title: 'Historical Context & Chronology' },
    { field: 'keyArchitecturalFeaturesLayout', title: 'Key Architectural Features / Layout' },
    { field: 'purposeAndFunction', title: 'Purpose and Function' },
    { field: 'culturalSignificance_narrative', title: 'Cultural Significance (Narrative)' },
    { field: 'notableFindsArtifacts_narrative', title: 'Notable Finds / Artifacts (Narrative)' },
    { field: 'theoriesAndInterpretations', title: 'Theories and Interpretations' },
    { field: 'currentStatusAndPreservation_narrative', title: 'Current Status and Preservation (Narrative)' },
];

export default function SiteInfo({
  site,
  media,
  onUpdate,
  onVisibilityChange
}) {
  // State
  const [isEditing, setIsEditing] = useState(false);
  const [editedSite, setEditedSite] = useState(site);
  const [editingPeriodIndex, setEditingPeriodIndex] = useState(null);
  const [hiddenLocations, setHiddenLocations] = useState({});
  const [showCitations, setShowCitations] = useState(false);

  // Context/Hooks
  const user = useSelector((state) => state.auth.user);
  const { isAdmin } = useAuth();
  const toast = useToast();

  // Responsive styles
  const sectionSpacing = useBreakpointValue({ base: 4, md: 6 });

  // Authorization
  const canEdit = useMemo(() => {
    return isAdmin || (user?.uid && user.uid === site.submittedBy);
  }, [isAdmin, user, site.submittedBy]);

  // Get the main image for the site
  const mainImage = useMemo(() => {
    if (site.mainImage) return site.mainImage;
    if (!media || media.length === 0) return null;
    const mainImg = media.find(m => m.isMain);
    if (mainImg) return mainImg.url;
    return media[0].url;
  }, [site.mainImage, media]);

  // Handlers
  const handleVisibilityChange = useCallback((newHiddenState) => {
    setHiddenLocations(newHiddenState);
  }, []);

  const handleToggleShowCitations = () => {
    setShowCitations(prev => !prev);
  };

  const handleEdit = () => {
    if (canEdit) {
      setEditedSite(site);
      setIsEditing(true);
    } else {
      toast({
        title: "Permission Denied",
        description: "Only administrators and site creators can edit site information",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleCancel = () => {
    setEditedSite(site);
    setIsEditing(false);
    setEditingPeriodIndex(null);
  };

  const handleFeatureToggle = async () => {
    try {
      const { success, error } = await updateSite(site.id, {
        featured: !site.featured
      });
      if (success) {
        toast({
          title: site.featured ? 'Removed from featured' : 'Added to featured',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        onUpdate?.({
          ...site,
          featured: !site.featured
        });
      } else {
        throw new Error(error);
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleSave = async () => {
    try {
      let introText = '';
      if (editedSite.overviewIntroduction && typeof editedSite.overviewIntroduction === 'object' && typeof editedSite.overviewIntroduction.value === 'string') {
        introText = editedSite.overviewIntroduction.value;
      } else if (typeof editedSite.overviewIntroduction === 'string') {
        introText = editedSite.overviewIntroduction;
      }
      const extractedDescription = getFirstNSentences(introText, 2);

      const siteDataToSave = {
        ...editedSite,
        description: extractedDescription
      };

      const { success, error } = await updateSite(site.id, siteDataToSave);
      if (success) {
        toast({
          title: 'Success',
          description: 'Site updated successfully',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        setIsEditing(false);
        onUpdate?.(siteDataToSave);
      } else {
        throw new Error(error);
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleEditChange = (field, value) => {
    setEditedSite(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleUpdateMainImage = async (mediaUrl) => {
    try {
      const updatedSite = {
        ...site,
        mainImage: mediaUrl
      };
      
      const { success, error } = await updateSite(site.id, updatedSite);
      if (success) {
        setEditedSite(prev => ({
          ...prev,
          mainImage: mediaUrl
        }));
        onUpdate?.(updatedSite);
        return true;
      } else {
        throw new Error(error);
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return false;
    }
  };

  return (
    <Box>
      {/* Hero Header */}
      <Box mb={{ base: 8, md: 12 }}>
        {/* Site Title and Actions */}
        <Flex
          direction={{ base: "column", md: "row" }}
          justify="space-between"
          align={{ base: "start", md: "center" }}
          mb={{ base: 6, md: 8 }}
          gap={{ base: 4, md: 0 }}
        >
          <Box>
            <Heading
              size={{ base: "2xl", md: "4xl" }}
              fontFamily="heading"
              color="text.default"
              lineHeight="shorter"
              textShadow="0 2px 4px rgba(0,0,0,0.1)"
              mb={2}
            >
              {site.name}
            </Heading>
            {site.featured && (
              <HStack spacing={2} mb={2}>
                <Star weight="fill" size={20} color="var(--chakra-colors-interactive-primary)" />
                <Text
                  fontSize="sm"
                  color="interactive.primary"
                  fontFamily="heading"
                  fontWeight="semibold"
                  textTransform="uppercase"
                  letterSpacing="wider"
                >
                  Featured Site
                </Text>
              </HStack>
            )}
          </Box>
          
          {canEdit && (
            <HStack spacing={3}>
              {!isEditing ? (
                <>
                  {isAdmin && (
                    <IconButton
                      icon={<Star weight={site.featured ? "fill" : "regular"} />}
                      aria-label={site.featured ? "Remove from featured" : "Add to featured"}
                      size="lg"
                      variant="ghost"
                      color={site.featured ? "interactive.primary" : "text.secondary"}
                      onClick={handleFeatureToggle}
                      bg="whiteAlpha.200"
                      _hover={{ bg: "whiteAlpha.300" }}
                      borderRadius="full"
                    />
                  )}
                  <IconButton
                    icon={<PencilSimpleLine size={24} />}
                    aria-label="Edit site information"
                    size="lg"
                    variant="solid"
                    colorScheme="brand.primary"
                    onClick={handleEdit}
                    borderRadius="full"
                    boxShadow="lg"
                    _hover={{ transform: "translateY(-2px)", boxShadow: "xl" }}
                    transition="all 0.2s ease"
                  />
                </>
              ) : (
                <>
                  <IconButton
                    icon={<XCircle size={24} />}
                    aria-label="Cancel editing"
                    size="lg"
                    variant="ghost"
                    colorScheme="red"
                    onClick={handleCancel}
                    borderRadius="full"
                    bg="whiteAlpha.200"
                    _hover={{ bg: "red.100" }}
                  />
                  <IconButton
                    icon={<CheckCircle size={24} />}
                    aria-label="Save changes"
                    size="lg"
                    variant="solid"
                    colorScheme="green"
                    onClick={handleSave}
                    borderRadius="full"
                    boxShadow="lg"
                    _hover={{ transform: "translateY(-2px)", boxShadow: "xl" }}
                    transition="all 0.2s ease"
                  />
                </>
              )}
            </HStack>
          )}
        </Flex>

        {/* Main Content Grid */}
        <Grid 
          templateColumns={{ base: "1fr", lg: "400px 1fr" }}
          gap={{ base: 6, md: 8, lg: 12 }}
          alignItems="start"
        >
          {/* Image Section */}
          <GridItem>
            <Box
              position="relative"
              borderRadius="2xl"
              overflow="hidden"
              boxShadow="2xl"
              bg="bg.surface"
              border="1px solid"
              borderColor="border.light"
              _hover={{
                transform: "translateY(-4px)",
                boxShadow: "3xl",
                transition: "all 0.3s ease"
              }}
              transition="all 0.3s ease"
            >
              <SiteImageManager
                site={site}
                media={media}
                mainImage={mainImage}
                canEdit={canEdit}
                onUpdateMainImage={handleUpdateMainImage}
              />
            </Box>
          </GridItem>

          {/* Info Section */}
          <GridItem>
            <Box
              bg="whiteAlpha.300"
              borderRadius="2xl"
              p={{ base: 6, md: 8 }}
              backdropFilter="blur(10px)"
              border="1px solid"
              borderColor="whiteAlpha.200"
              boxShadow="xl"
            >
              <SiteBasicInfo
                site={site}
                editedSite={editedSite}
                isEditing={isEditing}
                onEditChange={handleEditChange}
                showCitations={showCitations}
                onToggleShowCitations={handleToggleShowCitations}
              />
            </Box>
          </GridItem>
        </Grid>
      </Box>

      {/* Detailed Content Section */}
      <Box
        bg="bg.surface"
        borderRadius="2xl"
        borderWidth="1px"
        borderColor="border.light"
        overflow="hidden"
        boxShadow="xl"
        mt={{ base: 8, md: 12 }}
      >
        <Grid templateColumns={{ base: "1fr", lg: "2fr 1fr" }} gap={0}>
          {/* Detailed Information */}
          <GridItem>
            <Box borderRight={{ lg: "1px solid" }} borderColor="border.light">
              {/* Header */}
              <Box
                bg="linear-gradient(135deg, interactive.primary 0%, accent.gold.600 100%)"
                px={{ base: 6, md: 8 }}
                py={5}
                borderBottom="1px solid"
                borderColor="border.light"
              >
                <HStack justifyContent="space-between" alignItems="center">
                  <Heading 
                    size={{ base: "lg", md: "xl" }} 
                    fontFamily="heading"
                    color="white"
                    textShadow="0 1px 2px rgba(0,0,0,0.1)"
                  >
                    Detailed Information
                  </Heading>
                  <HStack alignItems="center" spacing={3}>
                    <Text
                      fontSize="sm"
                      color="whiteAlpha.900"
                      fontFamily="heading"
                      fontWeight="medium"
                    >
                      Citations
                    </Text>
                    <Switch
                      id="toggle-citations"
                      isChecked={showCitations}
                      onChange={handleToggleShowCitations}
                      size="md"
                      colorScheme="whiteAlpha"
                    />
                  </HStack>
                </HStack>
              </Box>

              {/* Content */}
              <Box p={{ base: 6, md: 8 }}>
                <Accordion allowToggle variant="unstyled">
                  {sectionsConfig.map(({ field, title }, index) => (
                    <AccordionItem 
                      key={field}
                      border="none"
                      mb={4}
                      bg="bg.accent"
                      borderRadius="xl"
                      overflow="hidden"
                      _last={{ mb: 0 }}
                    >
                      <h2>
                        <AccordionButton
                          px={6}
                          py={4}
                          _hover={{
                            bg: "whiteAlpha.100"
                          }}
                          _expanded={{
                            bg: "interactive.primary",
                            color: "white"
                          }}
                          transition="all 0.2s ease"
                        >
                          <Box flex="1" textAlign="left">
                            <Text
                              fontFamily="heading"
                              fontWeight="semibold"
                              fontSize={{ base: "md", md: "lg" }}
                            >
                              {title}
                            </Text>
                          </Box>
                          <AccordionIcon />
                        </AccordionButton>
                      </h2>
                      <AccordionPanel 
                        px={6} 
                        py={5}
                        bg="bg.default"
                        borderTop="1px solid"
                        borderColor="border.light"
                      >
                        {isEditing ? (
                          <Textarea
                            variant="journal"
                            value={editedSite[field]?.value || ''}
                            onChange={(e) => handleEditChange(field, { ...editedSite[field], value: e.target.value })}
                            minH="150px"
                            resize="vertical"
                          />
                        ) : (
                          <Box
                            fontFamily="body"
                            fontSize="md"
                            lineHeight="tall"
                            color="text.default"
                          >
                            <SourceLinkedText
                              text={renumberInTextCitations(site[field]?.value || '', site.citations || [])}
                              groundingMetadata={site[field]?.groundingMetadata || {}}
                              citations={site.citations || []}
                              showCitations={showCitations}
                            />
                          </Box>
                        )}
                      </AccordionPanel>
                    </AccordionItem>
                  ))}
                </Accordion>
              </Box>
            </Box>
          </GridItem>

          {/* Timeline Section */}
          <GridItem>
            <Box h="full">
              {/* Header */}
              <Box
                bg="linear-gradient(135deg, accent.gold.500 0%, interactive.primary 100%)"
                px={{ base: 6, md: 8 }}
                py={5}
                borderBottom="1px solid"
                borderColor="border.light"
              >
                <Heading 
                  size={{ base: "lg", md: "xl" }} 
                  fontFamily="heading"
                  color="white"
                  textShadow="0 1px 2px rgba(0,0,0,0.1)"
                >
                  Construction Timeline
                </Heading>
              </Box>

              {/* Content */}
              <Box p={{ base: 6, md: 8 }} h="full">
                <SiteTimeline
                  site={site}
                  editedSite={editedSite}
                  isEditing={isEditing}
                  showCitations={showCitations}
                  editingPeriodIndex={editingPeriodIndex}
                  onSetEditingPeriod={index => setEditingPeriodIndex(index)}
                  onUpdatePeriod={periods => handleEditChange('constructionPeriods', periods)}
                  onAddPeriod={() => {
                    const newPeriod = { periodName: "New Construction Phase", startEra: "", endEra: "", culture: "", features: [] };
                    const currentPeriods = editedSite.constructionPeriods || [];
                    const updatedPeriods = [...currentPeriods, newPeriod];
                    handleEditChange('constructionPeriods', updatedPeriods);
                    setEditingPeriodIndex(updatedPeriods.length - 1);
                  }}
                  onSetMainPeriod={index => handleEditChange('mainPeriodIndex', index)}
                  onDeletePeriod={index => {
                    const updatedPeriods = [...(editedSite.constructionPeriods || [])];
                    updatedPeriods.splice(index, 1);
                    handleEditChange('constructionPeriods', updatedPeriods);
                    if (editingPeriodIndex === index) {
                      setEditingPeriodIndex(null);
                    }
                  }}
                  onAddFeature={(periodIndex, feature) => {
                    if (!feature.trim()) return;
                    const updatedPeriods = [...(editedSite.constructionPeriods || [])];
                    updatedPeriods[periodIndex].features = [...(updatedPeriods[periodIndex].features || []), feature.trim()];
                    handleEditChange('constructionPeriods', updatedPeriods);
                  }}
                  onRemoveFeature={(periodIndex, featureIndex) => {
                    const updatedPeriods = [...(editedSite.constructionPeriods || [])];
                    const features = [...updatedPeriods[periodIndex].features];
                    features.splice(featureIndex, 1);
                    updatedPeriods[periodIndex].features = features;
                    handleEditChange('constructionPeriods', updatedPeriods);
                  }}
                />
              </Box>
            </Box>
          </GridItem>
        </Grid>
      </Box>

      {/* Location Section */}
      {site.location?.coordinates && (
        <Box
          bg="bg.surface"
          borderRadius="2xl"
          borderWidth="1px"
          borderColor="border.light"
          overflow="hidden"
          boxShadow="xl"
          mt={{ base: 8, md: 12 }}
          _hover={{
            boxShadow: "2xl",
            transform: "translateY(-2px)",
            transition: "all 0.3s ease"
          }}
          transition="all 0.3s ease"
        >
          {/* Header */}
          <Box
            bg="linear-gradient(135deg, map.routes 0%, interactive.primary 100%)"
            px={{ base: 6, md: 8 }}
            py={5}
            borderBottom="1px solid"
            borderColor="border.light"
          >
            <Heading 
              size={{ base: "lg", md: "xl" }} 
              fontFamily="heading"
              color="white"
              textShadow="0 1px 2px rgba(0,0,0,0.1)"
            >
              Interactive Location Map
            </Heading>
          </Box>

          {/* Content */}
          <Box p={{ base: 4, md: 6 }}>
            <SiteLocationSection
              site={site}
              media={media}
              hiddenLocations={hiddenLocations}
              onVisibilityChange={handleVisibilityChange}
            />
          </Box>
        </Box>
      )}

      <SiteGroundingInfo site={site} />
    </Box>
  );
}
